import { message } from "antd";
import { useEffect, useRef, useState } from "react";
import { envStorage } from "@/lib/storage/envIsolatedStorage";
import { refreshSession } from "@/services/session.service";

const STORAGE_KEY = "debug_session_refresh_enabled";
const INTERVAL_MS = 13 * 60 * 1000; // 13 minutes in milliseconds

/**
 * Hook to manage automatic session refresh timer
 * Controlled by DebugPanel switch, refreshes session every 13 minutes when enabled
 */
export const useSessionRefreshTimer = () => {
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);
  const [isEnabled, setIsEnabled] = useState(() => {
    return envStorage.getItem(STORAGE_KEY) === "true";
  });

  // Listen for custom events to update enabled state
  useEffect(() => {
    const handleStorageChange = () => {
      const newValue = envStorage.getItem(STORAGE_KEY) === "true";
      setIsEnabled(newValue);
    };

    // Listen for custom event dispatched when localStorage changes
    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("session-refresh-toggle", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("session-refresh-toggle", handleStorageChange);
    };
  }, []);

  // Manage the timer based on enabled state
  useEffect(() => {
    // Clear any existing timer first
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
    }

    if (!isEnabled) {
      console.log("[Session Refresh Timer] Timer stopped (disabled)");
      return;
    }

    // Start the timer
    console.log("[Session Refresh Timer] Timer started - will refresh every 13 minutes");

    const refreshSessionTask = async () => {
      const timestamp = new Date().toISOString();
      console.log(`[Session Refresh Timer] ${timestamp} - Triggering session refresh...`);

      try {
        const result = await refreshSession();
        console.log(
          `[Session Refresh Timer] ${timestamp} - Session refreshed successfully:`,
          result,
        );
        message.success("Session refreshed successfully (auto-refresh)");
      } catch (error) {
        console.error(`[Session Refresh Timer] ${timestamp} - Failed to refresh session:`, error);
        message.error("Failed to refresh session (auto-refresh)");
      }
    };

    intervalIdRef.current = setInterval(refreshSessionTask, INTERVAL_MS);

    // Cleanup function to clear interval when component unmounts or enabled state changes
    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
        console.log("[Session Refresh Timer] Timer cleared (cleanup)");
      }
    };
  }, [isEnabled]);
};
